/**
 * The "+n" dropdown of an avatar quick-filter row: a teleported panel listing
 * every option of the filter as a checkbox (multi) or radio (single) row.
 * The selection is kept locally while the panel is open — a click updates
 * the row's input at once and sends the same `toggleQuickFilterChoice()`
 * the avatar buttons send — and is read from the server-rendered
 * `data-selected` attribute on init and every time the panel opens, so a
 * value applied elsewhere (an avatar click, the editor) is reflected without
 * keeping the panel in sync through morphs. The x-data expression itself
 * carries only constants (a changed expression would re-initialise the
 * component on morph and close the open panel).
 *
 * Positioned like the select overlay: fixed, viewport coordinates, below the
 * button unless there is more room above; re-measured on scroll and resize
 * and closed when the button scrolls out of view.
 */
export default ({ queryName, single = false, selected = [], width = 288 } = {}) => ({
    open: false,
    single,
    queryName,
    selected: [...selected].map(String),
    panelTop: 0,
    panelLeft: 0,
    panelWidth: width,
    openUpwards: false,
    _reposition: null,
    _navigateRef: null,
    _pointerAt: 0,
    _focusTimer: null,

    init() {
        this.syncSelected();

        this._navigateRef = () => this.close();
        window.addEventListener('livewire:navigate', this._navigateRef);
        window.addEventListener('livewire:navigating', this._navigateRef);
    },

    destroy() {
        this.stopFocusWait();
        this.stopTracking();

        if (this._navigateRef) {
            window.removeEventListener('livewire:navigate', this._navigateRef);
            window.removeEventListener('livewire:navigating', this._navigateRef);
            this._navigateRef = null;
        }
    },

    toggle() {
        this.open ? this.close(true) : this.show();
    },

    show() {
        const button = this.$refs?.button;
        if (!button || !button.isConnected) return;

        this.syncSelected();
        this.open = true;
        this.updatePosition();
        this.startTracking();

        // The panel lives in an x-teleport and is revealed by x-show. WebKit can expose the
        // teleported ref before it has a rendered box, so the bounded retry below waits for the
        // actual node instead of focusing a still-hidden input (which Safari silently ignores).
        this.stopFocusWait();
        this._focusTimer = setTimeout(() => {
            this._focusTimer = null;
            this.focusWhenReady();
        }, 0);
    },

    close(focusButton = false) {
        this.stopFocusWait();
        if (!this.open) return;

        this.open = false;
        this.stopTracking();

        if (focusButton) {
            this.$refs?.button?.focus?.();
        }
    },

    /**
     * The focus leaving the panel — Tab out of the last row (the teleported
     * panel is the last thing in the document, so the focus leaves the page
     * and relatedTarget is null), Shift+Tab out of the first — closes it,
     * unless it moves to the button (its toggle) or stays inside. A pointer
     * pressed inside the panel (a click on its padding) also blurs a row
     * without a relatedTarget; that is remembered for the moment and ignored.
     */
    focusLeft(event) {
        if (!this.open) return;

        const target = event?.relatedTarget ?? null;
        const panel = this.$refs?.panel;
        const button = this.$refs?.button;

        if (target === null) {
            if (Date.now() - this._pointerAt < 300) return;
        } else if (panel?.contains?.(target) || target === button || button?.contains?.(target)) {
            return;
        }

        this.close();
    },

    /** A pointer pressed inside the panel: the focusout right after it is no exit. */
    pointerInside() {
        this._pointerAt = Date.now();
    },

    /** A click outside the panel closes it; the button's own click is its toggle. */
    clickedOutside(event) {
        if (!this.open) return;

        const button = this.$refs?.button;
        if (button && (event.target === button || button.contains?.(event.target))) return;

        this.close();
    },

    /** Re-read the applied values the server rendered into data-selected. */
    syncSelected() {
        const raw = this.$root?.dataset?.selected;
        if (raw === undefined) return;

        try {
            const values = JSON.parse(raw);
            this.selected = Array.isArray(values) ? values.map(String) : [];
        } catch {
            this.selected = [];
        }
    },

    isSelected(value) {
        return this.selected.includes(String(value));
    },

    /**
     * Same semantics as the avatar buttons: a multi-value filter toggles the
     * value in and out of the list, a single-value filter replaces its value
     * or clears it when the selected value is clicked again.
     */
    toggleValue(value) {
        const key = String(value);

        if (this.single) {
            this.selected = this.isSelected(key) ? [] : [key];
        } else if (this.isSelected(key)) {
            this.selected = this.selected.filter((item) => item !== key);
        } else {
            this.selected = [...this.selected, key];
        }

        this.$wire?.toggleQuickFilterChoice?.(this.queryName, value);
    },

    focusFirst() {
        const panel = this.$refs?.panel;
        if (!panel) return;

        const target = panel.querySelector('input[type="search"]')
            || panel.querySelector('input:checked')
            || panel.querySelector('input');

        target?.focus?.();
    },

    focusWhenReady(attempt = 0) {
        if (!this.open) return;

        const panel = this.$refs?.panel;
        const rendered = panel
            && (typeof panel.getClientRects !== 'function' || panel.getClientRects().length > 0);

        if (rendered) {
            this.updatePosition();
            if (this.open) this.focusFirst();
            return;
        }

        if (attempt >= 20) {
            // Keep the wait bounded, but preserve the old best-effort focus when an engine
            // reports no client rect even though the control has become focusable.
            this.updatePosition();
            if (this.open) this.focusFirst();
            return;
        }

        this._focusTimer = setTimeout(() => {
            this._focusTimer = null;
            this.focusWhenReady(attempt + 1);
        }, 16);
    },

    stopFocusWait() {
        if (this._focusTimer === null) return;

        clearTimeout(this._focusTimer);
        this._focusTimer = null;
    },

    updatePosition() {
        if (!this.open) return;

        const button = this.$refs?.button;
        if (!button || !button.isConnected) {
            this.close();
            return;
        }

        const rect = button.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        if (rect.bottom <= 0 || rect.top >= viewportHeight) {
            this.close();
            return;
        }

        const panelHeight = this.$refs?.panel?.offsetHeight || 240;
        const availableBelow = viewportHeight - rect.bottom;
        const availableAbove = rect.top;

        this.openUpwards = availableBelow < panelHeight && availableAbove > availableBelow;
        this.panelTop = this.openUpwards ? Math.max(0, rect.top - panelHeight) : rect.bottom;
        this.panelLeft = Math.max(0, Math.min(rect.left, viewportWidth - this.panelWidth));
    },

    startTracking() {
        if (this._reposition) return;

        this._reposition = () => this.updatePosition();
        window.addEventListener('scroll', this._reposition, true);
        window.addEventListener('resize', this._reposition);
    },

    stopTracking() {
        if (!this._reposition) return;

        window.removeEventListener('scroll', this._reposition, true);
        window.removeEventListener('resize', this._reposition);
        this._reposition = null;
    },
})
